import type { WeekData, TransactionDetail } from '../types/planner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea, BarChart, Bar } from 'recharts';
import { useState } from 'react';
import '../../styles/planner.css';

interface PlannerTableProps {
  weeks: WeekData[];
  onAdjustmentChange?: (date: string, value: number) => void; // Optional for backward compatibility
  bufferValue: number;
  startingBalance: number;
  goals?: Array<{ name: string; value: number }>;
  onNavigateToAdjustments?: (date: string) => void; // Callback to navigate to adjustments page
  currency?: string; // Optional, defaults to 'USD'
  /** 0 = Sunday … 6 = Saturday; matches General settings “Start on Day of Week” */
  weekStartDay?: number;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

function weekStartDayLabel(weekStartDay: number): string {
  if (weekStartDay >= 0 && weekStartDay <= 6) {
    return WEEKDAY_NAMES[weekStartDay];
  }
  return WEEKDAY_NAMES[0];
}

const COLUMN_TOOLTIPS: Record<string, string> = {
  'Income': 'Total recurring income for this week, calculated from recurring transactions or budgets categorized as Income. Ensure income categories, e.g., Salary, have been designated as Income category types.',
  'Debt': 'Total debt payments for this week, including loan payments, credit card payments, and other debt obligations. Debt payments are typically reflected as positive value transactions in liability accounts, but are negative cashflow events, and should be categorized as such.',
  'Non-Discretionary': 'Bills, necessities, and non-negotiable costs of services. These are expenses you must pay, such as rent, utilities, insurance, and subscriptions, for which the costs are typically fixed or pre-determined.',
  'Discretionary': 'Total discretionary expenses (optional spending or negotiable amounts)',
  'Expense': 'Sum of Non-Discretionary + Discretionary + Debt',
  'Budgeted': 'Income - Expense (projected net for this week)',
  'Budgeted To Date': 'Cumulative sum of all Budgeted values from the start date to this week',
  'Adjustment': 'Manual adjustment to the budgeted net. Use this to account for one-time expenses, income changes, or other financial events, or scenario testing, not captured in your recurring transactions or budgets.',
  'Adjusted Net': 'Budgeted + Adjustment (weekly net cash flow after adjustments)',
  'Balance': 'Last week Balance + Adjusted Net',
  'Balance-Buffer': 'Balance minus the buffer amount',

};

interface TableRow {
  type: 'week' | 'year-divider';
  week?: WeekData;
  year?: number;
}

function detailColumnTintKey(columnName: string): string {
  switch (columnName) {
    case 'Income':
      return 'income';
    case 'Debt':
      return 'debt';
    case 'Non-Discretionary':
      return 'non-discretionary';
    case 'Discretionary':
      return 'discretionary';
    case 'Adjustment':
      return 'adjustment';
    default:
      return 'income';
  }
}

interface DetailTableProps {
  details: TransactionDetail[];
  columnName: string;
  currency?: string; // Optional, defaults to 'USD'
}

function DetailTable({ details, columnName, currency = 'USD' }: DetailTableProps) {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getCurrencySymbol = (currencyCode: string): string => {
    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      // Format a zero value to extract the currency symbol
      const parts = formatter.formatToParts(0);
      const symbolPart = parts.find(part => part.type === 'currency');
      return symbolPart ? symbolPart.value : '$';
    } catch {
      return '$'; // Fallback to $ if currency code is invalid
    }
  };

  const formatSource = (source: string): string => {
    switch (source) {
      case 'budget':
        return 'Bgt';
      case 'recurring':
        return 'Rcr';
      case 'adjustment':
        return 'Adj';
      default:
        return source;
    }
  };

  if (details.length === 0) {
    return null;
  }

  const tintKey = detailColumnTintKey(columnName);

  return (
    <div className={`column-detail-table column-detail--${tintKey}`}>
      <table className="detail-table">
        <thead>
          <tr>
            <th>Payee</th>
            <th>Category</th>
            <th>{getCurrencySymbol(currency)}</th>
            {columnName !== 'Adjustment' && <th>Src</th>}
            {columnName === 'Adjustment' && <th>Note</th>}
          </tr>
        </thead>
        <tbody>
          {details.map((detail, index) => (
            <tr
              key={index}
              className={detail.alreadyAccountedInBalance ? 'detail-row--already-in-balance' : undefined}
            >
              <td>{detail.payee}</td>
              <td>{detail.category}</td>
              <td className={detail.amount >= 0 ? 'positive' : 'negative'}>
                {formatCurrency(detail.amount)}
              </td>
              {columnName !== 'Adjustment' && (
                <td className="source-cell">{formatSource(detail.source)}</td>
              )}
              {columnName === 'Adjustment' && (
                <td className="note-cell">{detail.note || ''}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PlannerTable({ weeks, bufferValue, startingBalance, goals = [], onNavigateToAdjustments, currency = 'USD', weekStartDay = 0 }: PlannerTableProps) {
  const dateColumnTooltip = `The date that marks the start of each week in the projection (week starts on ${weekStartDayLabel(weekStartDay)})`;
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'balance' | 'spending'>('balance');
  const [lineVisibility, setLineVisibility] = useState({
    'Budgeted To Date': false,
    'Balance': true,
    'Balance-Buffer': false,
  });
  const [goalVisibility, setGoalVisibility] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = { 'Buffer': true };
    goals.forEach(goal => {
      if (goal.name && goal.name !== 'Buffer') {
        initial[goal.name] = true;
      }
    });
    return initial;
  });

  const toggleAll = () => {
    if (expandedRows.size > 0) {
      // Collapse all
      setExpandedRows(new Set());
    } else {
      // Expand all
      const allDates = new Set(weeks.map(week => week.date));
      setExpandedRows(allDates);
    }
  };

  const hasExpandedRows = expandedRows.size > 0;

  // Calculate minimums and when they occur
  const findMinimums = () => {
    let bufferMinValue = Infinity;
    let bufferMinWeek: WeekData | null = null;
    let bufferMinWeekNumber = 0;
    
    let minValue = Infinity;
    let minWeek: WeekData | null = null;
    let minWeekNumber = 0;
    
    weeks.forEach((week, index) => {
      if (week.balanceBuffer < bufferMinValue) {
        bufferMinValue = week.balanceBuffer;
        bufferMinWeek = week;
        bufferMinWeekNumber = index + 1;
      }
      
      if (week.balance < minValue) {
        minValue = week.balance;
        minWeek = week;
        minWeekNumber = index + 1;
      }
    });
    
    return {
      bufferMin: bufferMinValue === Infinity ? 0 : bufferMinValue,
      bufferMinWeek: bufferMinWeek as WeekData | null,
      bufferMinWeekNumber,
      min: minValue === Infinity ? 0 : minValue,
      minWeek: minWeek as WeekData | null,
      minWeekNumber,
    };
  };
  
  const minimums = findMinimums();
  
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

    const formatYAxisLabel = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };


  const formatDate = (dateStr: string): string => {
    // Parse YYYY-MM-DD as local date to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayStr = date.getDate().toString().padStart(2, "0");
    const monthStr = date.toLocaleDateString("en-US", { month: "short" });
    return `${dayStr}-${monthStr}`;
  };

  /** e.g. Thu Apr 9 — used when the row is expanded for detail view */
  const formatDateWithWeekday = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
    const dayNum = date.getDate();
    return `${weekday} ${monthShort} ${dayNum}`;
  };

  const getYear = (dateStr: string): number => {
    // Parse YYYY-MM-DD as local date to avoid timezone issues
    const [year] = dateStr.split('-').map(Number);
    return year;
  };

  // Create rows with year dividers
  const createRows = (): TableRow[] => {
    const rows: TableRow[] = [];
    let currentYear: number | null = null;

    weeks.forEach((week) => {
      const weekYear = getYear(week.date);
      
      // Insert year divider if year changed
      if (currentYear !== null && weekYear !== currentYear) {
        rows.push({ type: 'year-divider', year: weekYear });
      }
      
      // Set current year on first row
      if (currentYear === null) {
        currentYear = weekYear;
        rows.push({ type: 'year-divider', year: weekYear });
      }
      
      rows.push({ type: 'week', week });
      currentYear = weekYear;
    });

    return rows;
  };

  const rows = createRows();

  const getCellClassName = (value: number): string => {
    if (value < 0) return 'negative';
    return 'positive';
  };

  const getBalanceCellClassName = (balance: number, bufferValue: number): string => {
    if (balance < 0) {
      return 'negative-dark'; // Dark red for negative values
    }
    if (balance < bufferValue) {
      return 'negative-light'; // Light red if below buffer threshold
    }
    return 'positive';
  };

  const getBalanceBufferCellClassName = (balanceBuffer: number, bufferValue: number): string => {
    if (balanceBuffer < 0) {
      return 'negative-dark'; // Dark red for negative values
    }
    if (balanceBuffer < bufferValue) {
      return 'negative-light'; // Light red if below buffer threshold
    }
    return 'positive';
  };

  const chartData = weeks.map((week, index) => ({
    week: index + 1,
    date: formatDate(week.date),
    'Budgeted To Date': week.budgetedNet,
    'Adjusted Net': week.adjustedNet,
    'Balance': week.balance,
    'Balance-Buffer': week.balanceBuffer,
    'Debt': week.debt,
    'Non-Discretionary': week.nonDiscretionary,
    'Discretionary': week.discretionary,
  }));

  // Calculate min and max values from visible data points only for Y-axis domain
  const visibleKeys = Object.entries(lineVisibility)
    .filter(([_, visible]) => visible)
    .map(([key, _]) => key as keyof typeof chartData[0]);
  
  const minValue = visibleKeys.length > 0
    ? Math.min(
        ...chartData.map(d => Math.min(
          ...visibleKeys.map(key => {
            const value = d[key];
            return typeof value === 'number' ? value : Infinity;
          })
        ))
      )
    : 0;
  const maxValue = visibleKeys.length > 0
    ? Math.max(
        ...chartData.map(d => Math.max(
          ...visibleKeys.map(key => {
            const value = d[key];
            return typeof value === 'number' ? value : -Infinity;
          })
        ))
      )
    : 0;

  // Add padding below the minimum and above the maximum data points (fixed $5000 padding)
  const padding = 5000;
  const yAxisMin = minValue - padding;
  const yAxisMax = maxValue + padding;

  // Calculate Y-axis ticks at even multiples of $1000 (for gridlines)
  const minTick = Math.floor(yAxisMin / 2000) * 2000;
  const maxTick = Math.ceil(yAxisMax / 2000) * 2000;
  const yAxisTicks: number[] = [];
  for (let i = minTick; i <= maxTick; i += 2000) {
    yAxisTicks.push(i);
  }

  // Find Buffer goal from goals array, fallback to bufferValue prop
  const bufferGoal = goals.find(goal => goal.name === 'Buffer' && goal.value !== null);
  const bufferGoalValue = bufferGoal?.value ?? bufferValue ?? null;

  // Calculate right margin based on longest label to ensure all labels are visible
  const rightSideLabels: string[] = [];
  if (bufferGoalValue !== null && goalVisibility['Buffer']) {
    rightSideLabels.push(`Buffer ${formatCurrency(bufferGoalValue)}`);
  }
  goals.forEach(goal => {
    if (goal.name !== 'Buffer' && goal.name && goal.value !== null && goalVisibility[goal.name]) {
      rightSideLabels.push(`${goal.name} ${formatCurrency(goal.value)}`);
    }
  });
  // Estimate width: ~8px per character + padding, minimum 120px
  const longestLabel = rightSideLabels.reduce((longest, label) => 
    label.length > longest.length ? label : longest, '');
  const rightMargin = Math.max(120, longestLabel.length * 8 + 40);


  return (
    <div className="planner-table-container">
      <div className="summary-section">
        <h3>Stats</h3>
        <div className="summary-items">
          <div className="summary-item">
            <span className="summary-label">Starting Balance:</span>
            <span className="summary-value">{formatCurrency(startingBalance)}</span>
            <span className="summary-date"></span>
          </div>
          {bufferGoalValue !== null && (
            <div className="summary-item">
              <span className="summary-label">Buffer Value:</span>
              <span className="summary-value">{formatCurrency(bufferGoalValue)}</span>
              <span className="summary-date"></span>
            </div>
          )}
          <div className="summary-item">
            <span className="summary-label">Buffer Min:</span>
            <span className="summary-value">{formatCurrency(minimums.bufferMin)}</span>
            <span className="summary-date">
              {minimums.bufferMinWeek ? `${formatDate(minimums.bufferMinWeek.date)} (Week ${minimums.bufferMinWeekNumber})` : 'N/A'}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Min:</span>
            <span className="summary-value">{formatCurrency(minimums.min)}</span>
            <span className="summary-date">
              {minimums.minWeek ? `${formatDate(minimums.minWeek.date)} (Week ${minimums.minWeekNumber})` : 'N/A'}
            </span>
          </div>
        </div>
      </div>
      <div className="chart-section-wrapper">
        <div className="chart-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e1e4e8' }}>
              <button
                onClick={() => setActiveTab('balance')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderBottom: activeTab === 'balance' ? '2px solid #0366d6' : '2px solid transparent',
                  color: activeTab === 'balance' ? '#0366d6' : '#586069',
                  fontWeight: activeTab === 'balance' ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '-2px',
                }}
              >
                Balance Trends
              </button>
              <button
                onClick={() => setActiveTab('spending')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderBottom: activeTab === 'spending' ? '2px solid #0366d6' : '2px solid transparent',
                  color: activeTab === 'spending' ? '#0366d6' : '#586069',
                  fontWeight: activeTab === 'spending' ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '-2px',
                }}
              >
                Spending Habits
              </button>
            </div>
          </div>
          <div className="chart-container">
            {activeTab === 'balance' && (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 5, right: rightMargin, left: 20, bottom: 5 }}>
              <CartesianGrid stroke="transparent" />
              <XAxis 
                dataKey="date" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval="preserveStartEnd"
                tickLine={false}
              />
              <YAxis domain={[yAxisMin, yAxisMax]} tickFormatter={formatYAxisLabel} ticks={yAxisTicks} tickLine={false} allowDataOverflow={true} tick={{ fontSize: 11 }} />
              <ReferenceArea y1={yAxisMin} y2={0} fill="#b71c1c" fillOpacity={0.4} />
              {bufferGoalValue !== null && (
                <>
                  <ReferenceArea y1={0} y2={bufferGoalValue} fill="#ffc0cb" fillOpacity={0.3} />
                  {goalVisibility['Buffer'] && (
                    <ReferenceLine y={bufferGoalValue} stroke="#ff9800" strokeWidth={1} label={{ value: `Buffer ${formatCurrency(bufferGoalValue)}`, position: 'right' }} />
                  )}
                </>
              )}
              {/* Goal lines */}
              {goals.map((goal, index) => {
                // Skip Buffer goal (already shown separately) and goals without names/values
                if (goal.name === 'Buffer' || !goal.name || goal.value === null) return null;
                if (!goalVisibility[goal.name]) return null;
                
                return (
                  <ReferenceLine 
                    key={`goal-${index}`}
                    y={goal.value} 
                    stroke="#ff9800"
                    strokeWidth={1}
                    label={{ value: `${goal.name} ${formatCurrency(goal.value)}`, position: 'right' }} 
                  />
                );
              })}
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  
                  // Order: Balance, Balance-Buffer, Budgeted To Date
                  // Filter to only show visible lines
                  const order = ['Balance', 'Balance-Buffer', 'Budgeted To Date'];
                  const orderedPayload = order
                    .map(key => payload.find(item => item.dataKey === key))
                    .filter(item => item !== undefined && lineVisibility[item.dataKey as keyof typeof lineVisibility]);
                  
                  // Find the matching data point to get the week number
                  const dataPoint = chartData.find(d => d.date === label);
                  const labelText = dataPoint ? `Week ${dataPoint.week} (${label})` : label;
                  
                  return (
                    <div style={{
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '10px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>{labelText}</p>
                      {orderedPayload.map((entry: any, index: number) => (
                        <p key={index} style={{ margin: '4px 0', color: entry.color }}>
                          <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: entry.color, marginRight: '8px' }}></span>
                          {entry.name}: {formatCurrency(Number(entry.value))}
                        </p>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend 
                content={({ payload }) => {
                  if (!payload || !payload.length) return null;
                  
                  // Order: Balance, Balance-Buffer, Budgeted To Date
                  // Filter to only show visible lines
                  const order = ['Balance', 'Balance-Buffer', 'Budgeted To Date'];
                  const orderedPayload = order
                    .map(key => payload.find(item => item.dataKey === key))
                    .filter(item => item !== undefined && lineVisibility[item.dataKey as keyof typeof lineVisibility]);
                  
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', padding: '10px 0' }}>
                      {orderedPayload.map((entry: any, index: number) => (
                        <span key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                          <span style={{ display: 'inline-block', width: '14px', height: '2px', backgroundColor: entry.color }}></span>
                          <span style={{ color: entry.color }}>{entry.value}</span>
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              {lineVisibility['Balance'] && <Line type="monotone" dataKey="Balance" stroke="#66bb6a" strokeWidth={2} dot={false} />}
              {lineVisibility['Balance-Buffer'] && <Line type="monotone" dataKey="Balance-Buffer" stroke="#1976d2" strokeWidth={2} dot={false} />}
              {lineVisibility['Budgeted To Date'] && <Line type="monotone" dataKey="Budgeted To Date" stroke="#00bcd4" strokeWidth={2} dot={false} />}
                </LineChart>
              </ResponsiveContainer>
            )}
            {activeTab === 'spending' && (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e1e4e8" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval="preserveStartEnd"
                    tickLine={false}
                  />
                  <YAxis tickFormatter={formatYAxisLabel} tickLine={false} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      
                      const dataPoint = chartData.find(d => d.date === label);
                      const labelText = dataPoint ? `Week ${dataPoint.week} (${label})` : label;
                      const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
                      
                      return (
                        <div style={{
                          backgroundColor: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '10px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>{labelText}</p>
                          {payload.map((entry: any, index: number) => (
                            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
                              <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: entry.color, marginRight: '8px' }}></span>
                              {entry.name}: {formatCurrency(Number(entry.value))}
                            </p>
                          ))}
                          <p style={{ margin: '8px 0 0 0', fontWeight: 600, borderTop: '1px solid #e1e4e8', paddingTop: '8px' }}>
                            Total: {formatCurrency(total)}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Debt" stackId="expenses" fill="#9b7fb8" />
                  <Bar dataKey="Non-Discretionary" stackId="expenses" fill="#b8865a" />
                  <Bar dataKey="Discretionary" stackId="expenses" fill="#6b8fa8" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="chart-control-panel">
          <h4 className="control-panel-title">Toggle Lines</h4>
          <div className="control-panel-checkboxes">
            <label className="control-panel-checkbox">
              <input
                type="checkbox"
                checked={lineVisibility['Balance']}
                onChange={(e) => setLineVisibility(prev => ({ ...prev, 'Balance': e.target.checked }))}
              />
              <span className="checkbox-label">
                <span className="checkbox-color-indicator" style={{ backgroundColor: '#66bb6a' }}></span>
                Balance
              </span>
            </label>
            <label className="control-panel-checkbox">
              <input
                type="checkbox"
                checked={lineVisibility['Balance-Buffer']}
                onChange={(e) => setLineVisibility(prev => ({ ...prev, 'Balance-Buffer': e.target.checked }))}
              />
              <span className="checkbox-label">
                <span className="checkbox-color-indicator" style={{ backgroundColor: '#1976d2' }}></span>
                Balance-Buffer
              </span>
            </label>
            <label className="control-panel-checkbox">
              <input
                type="checkbox"
                checked={lineVisibility['Budgeted To Date']}
                onChange={(e) => setLineVisibility(prev => ({ ...prev, 'Budgeted To Date': e.target.checked }))}
              />
              <span className="checkbox-label">
                <span className="checkbox-color-indicator" style={{ backgroundColor: '#00bcd4' }}></span>
                Budgeted To Date
              </span>
            </label>
          </div>
          <h4 className="control-panel-title" style={{ marginTop: '20px' }}>Toggle Goals</h4>
          <div className="control-panel-checkboxes">
            <label className="control-panel-checkbox">
              <input
                type="checkbox"
                checked={goalVisibility['Buffer'] ?? false}
                onChange={(e) => setGoalVisibility(prev => ({ ...prev, 'Buffer': e.target.checked }))}
              />
              <span className="checkbox-label">
                <span className="checkbox-color-indicator" style={{ backgroundColor: '#ff9800' }}></span>
                Buffer
              </span>
            </label>
            {goals.map((goal) => {
              if (goal.name === 'Buffer' || !goal.name) return null;
              return (
                <label key={goal.name} className="control-panel-checkbox">
                  <input
                    type="checkbox"
                    checked={goalVisibility[goal.name] ?? false}
                    onChange={(e) => setGoalVisibility(prev => ({ ...prev, [goal.name]: e.target.checked }))}
                  />
                  <span className="checkbox-label">
                    <span className="checkbox-color-indicator" style={{ backgroundColor: '#ff9800' }}></span>
                    {goal.name}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
      <table className="planner-table">
        <thead>
          <tr>
            <th className="toggle-column-header">
              <button 
                className="toggle-all-button" 
                onClick={toggleAll}
                title={hasExpandedRows ? "Collapse all rows" : "Expand all rows"}
                aria-label={hasExpandedRows ? "Collapse all rows" : "Expand all rows"}
              >
                {hasExpandedRows ? '▼▼' : '▶▶'}
              </button>
            </th>
            <th title={dateColumnTooltip}>Date</th>
            <th title={COLUMN_TOOLTIPS['Income']}>Income</th>
            <th title={COLUMN_TOOLTIPS['Debt']}>Debt</th>
            <th title={COLUMN_TOOLTIPS['Non-Discretionary']}>Non-Discretionary</th>
            <th title={COLUMN_TOOLTIPS['Discretionary']}>Discretionary</th>
            <th title={COLUMN_TOOLTIPS['Expense']}>Expense</th>
            <th title={COLUMN_TOOLTIPS['Budgeted']}>Budgeted</th>
            <th title={COLUMN_TOOLTIPS['Budgeted To Date']}>Budgeted To Date</th>
            <th title={COLUMN_TOOLTIPS['Adjustment']}>Adjustment</th>
            <th title={COLUMN_TOOLTIPS['Adjusted Net']}>Adjusted Net</th>
            <th title={COLUMN_TOOLTIPS['Balance']}>Balance</th>
            <th title={COLUMN_TOOLTIPS['Balance-Buffer']}>Balance-Buffer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.type === 'year-divider') {
              return (
                <tr key={`year-${row.year}`} className="year-divider-row">
                  <td colSpan={13} className="year-divider-cell">
                    {row.year}
                  </td>
                </tr>
              );
            }

            const week = row.week!;
            const isExpanded = expandedRows.has(week.date);
            
            const incomeDetails = (week as any).incomeDetails || [];
            const debtDetails = (week as any).debtDetails || [];
            const nonDiscretionaryDetails = (week as any).nonDiscretionaryDetails || [];
            const discretionaryDetails = (week as any).discretionaryDetails || [];
            const adjustmentDetails = (week as any).adjustmentDetails || [];
            
            // Check if all detail columns are empty
            const hasAnyDetails = incomeDetails.length > 0 || 
                                 debtDetails.length > 0 || 
                                 nonDiscretionaryDetails.length > 0 || 
                                 discretionaryDetails.length > 0 || 
                                 adjustmentDetails.length > 0;
            
            const toggleRow = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (!hasAnyDetails) return; // Don't toggle if no details
              setExpandedRows(prev => {
                const newExpanded = new Set(prev);
                if (newExpanded.has(week.date)) {
                  newExpanded.delete(week.date);
                } else {
                  newExpanded.add(week.date);
                }
                return newExpanded;
              });
            };

            return (
              <tr key={week.date} className={isExpanded ? 'expanded-row' : ''}>
                <td 
                  className={`toggle-column-cell ${!hasAnyDetails ? 'disabled' : ''}`} 
                  onClick={toggleRow}
                  title={!hasAnyDetails ? 'No transaction details available' : undefined}
                >
                  <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                </td>
                <td className="date-cell">
                  {isExpanded ? formatDateWithWeekday(week.date) : formatDate(week.date)}
                </td>
                <td className={`${getCellClassName(week.income)} detail-column`}>
                  <div className="cell-value">{formatCurrency(week.income)}</div>
                  <div className={`column-details ${isExpanded && incomeDetails.length > 0 ? 'visible' : ''}`}>
                    {isExpanded && incomeDetails.length > 0 && (
                      <DetailTable details={incomeDetails} columnName="Income" currency={currency} />
                    )}
                  </div>
                </td>
                <td className={`${getCellClassName(week.debt)} detail-column`}>
                  <div className="cell-value">{formatCurrency(week.debt)}</div>
                  <div className={`column-details ${isExpanded && debtDetails.length > 0 ? 'visible' : ''}`}>
                    {isExpanded && debtDetails.length > 0 && (
                      <DetailTable details={debtDetails} columnName="Debt" currency={currency} />
                    )}
                  </div>
                </td>
                <td className={`${getCellClassName(week.nonDiscretionary)} detail-column`}>
                  <div className="cell-value">{formatCurrency(week.nonDiscretionary)}</div>
                  <div className={`column-details ${isExpanded && nonDiscretionaryDetails.length > 0 ? 'visible' : ''}`}>
                    {isExpanded && nonDiscretionaryDetails.length > 0 && (
                      <DetailTable details={nonDiscretionaryDetails} columnName="Non-Discretionary" currency={currency} />
                    )}
                  </div>
                </td>
                <td className={`${getCellClassName(week.discretionary)} detail-column`}>
                  <div className="cell-value">{formatCurrency(week.discretionary)}</div>
                  <div className={`column-details ${isExpanded && discretionaryDetails.length > 0 ? 'visible' : ''}`}>
                    {isExpanded && discretionaryDetails.length > 0 && (
                      <DetailTable details={discretionaryDetails} columnName="Discretionary" currency={currency} />
                    )}
                  </div>
                </td>
                <td className={getCellClassName(week.expense)}>{formatCurrency(week.expense)}</td>
                <td className={getCellClassName(week.budgeted)}>{formatCurrency(week.budgeted)}</td>
                <td className={getCellClassName(week.budgetedNet)}>
                  {formatCurrency(week.budgetedNet)}
                </td>
                <td 
                  className={`adjustment-cell ${getCellClassName(week.adjustment)} detail-column`}
                  onDoubleClick={() => {
                    if (onNavigateToAdjustments) {
                      onNavigateToAdjustments(week.date);
                    }
                  }}
                  style={{ cursor: onNavigateToAdjustments ? 'pointer' : 'default' }}
                  title={onNavigateToAdjustments ? 'Double-click to edit adjustments' : undefined}
                >
                  <div className="cell-value">{formatCurrency(week.adjustment)}</div>
                  <div className={`column-details ${isExpanded && adjustmentDetails.length > 0 ? 'visible' : ''}`}>
                    {isExpanded && adjustmentDetails.length > 0 && (
                      <DetailTable details={adjustmentDetails} columnName="Adjustment" currency={currency} />
                    )}
                  </div>
                </td>
                <td className={getCellClassName(week.adjustedNet)}>
                  {formatCurrency(week.adjustedNet)}
                </td>
                <td className={getBalanceCellClassName(week.balance, bufferValue)}>
                  {formatCurrency(week.balance)}
                </td>
                <td className={getBalanceBufferCellClassName(week.balanceBuffer, bufferValue)}>
                  {formatCurrency(week.balanceBuffer)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
